<script setup>
import { ref, computed } from 'vue';
import { useStoreGetters } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  message: {
    type: Object,
    required: false,
    default: null,
  },
  messageCount: {
    type: Number,
    required: false,
    default: 1,
  },
});

const emit = defineEmits(['close', 'forward']);
const getters = useStoreGetters();
const { t } = useI18n();

const searchQuery = ref('');
const selectedConversation = ref(null);

const allConversations = computed(() => getters.getAllConversations.value);

const filteredConversations = computed(() => {
  const query = searchQuery.value.toLowerCase().trim();

  if (!query) {
    return allConversations.value;
  }

  return allConversations.value.filter(conversation => {
    const contactName = conversation.meta?.sender?.name?.toLowerCase() || '';
    const contactEmail = conversation.meta?.sender?.email?.toLowerCase() || '';
    const contactPhone =
      conversation.meta?.sender?.phone_number?.toLowerCase() || '';

    return (
      contactName.includes(query) ||
      contactEmail.includes(query) ||
      contactPhone.includes(query)
    );
  });
});

const handleSelectConversation = conversation => {
  selectedConversation.value = conversation;
};

const handleForward = () => {
  if (selectedConversation.value) {
    emit('forward', selectedConversation.value.id);
  }
};

const handleClose = () => {
  emit('close');
};

const getContactName = conversation => {
  return (
    conversation.meta?.sender?.name ||
    t('CONVERSATION.FORWARD_MODAL.UNKNOWN_CONTACT')
  );
};

const getContactIdentifier = conversation => {
  const email = conversation.meta?.sender?.email;
  const phone = conversation.meta?.sender?.phone_number;
  return email || phone || '';
};

const modalTitle = computed(() => {
  if (props.messageCount > 1) {
    return t('CONVERSATION.FORWARD_MODAL.TITLE_MULTIPLE', {
      count: props.messageCount,
    });
  }
  return t('CONVERSATION.FORWARD_MODAL.TITLE');
});
</script>

<template>
  <woot-modal show :on-close="handleClose">
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header :header-title="modalTitle" />

      <div class="flex-1 overflow-hidden flex flex-col">
        <div class="p-4 border-b border-n-strong">
          <div class="relative">
            <fluent-icon
              icon="search"
              size="16"
              class="absolute left-3 top-1/2 -translate-y-1/2 text-n-slate-11"
            />
            <input
              v-model="searchQuery"
              type="text"
              :placeholder="$t('CONVERSATION.FORWARD_MODAL.SEARCH_PLACEHOLDER')"
              class="w-full pl-10 pr-4 py-2 border border-n-strong rounded-lg bg-n-background text-n-slate-12 placeholder-n-slate-11 focus:outline-none focus:ring-2 focus:ring-n-blue-9"
            />
          </div>
        </div>

        <div class="flex-1 overflow-y-auto p-4">
          <div
            v-if="filteredConversations.length === 0"
            class="text-center py-8 text-n-slate-11"
          >
            {{ $t('CONVERSATION.FORWARD_MODAL.NO_RESULTS') }}
          </div>

          <div
            v-for="conversation in filteredConversations"
            :key="conversation.id"
            class="flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors mb-2"
            :class="{
              'bg-n-blue-3 border border-n-blue-9':
                selectedConversation?.id === conversation.id,
              'hover:bg-n-alpha-2':
                selectedConversation?.id !== conversation.id,
            }"
            @click="handleSelectConversation(conversation)"
          >
            <div class="flex-shrink-0">
              <img
                v-if="conversation.meta?.sender?.thumbnail"
                :src="conversation.meta.sender.thumbnail"
                :alt="getContactName(conversation)"
                class="w-10 h-10 rounded-full"
              />
              <div
                v-else
                class="w-10 h-10 rounded-full bg-n-blue-9 flex items-center justify-center text-white font-semibold"
              >
                {{ getContactName(conversation).charAt(0).toUpperCase() }}
              </div>
            </div>

            <div class="flex-1 min-w-0">
              <div class="font-medium text-n-slate-12 truncate">
                {{ getContactName(conversation) }}
              </div>
              <div
                v-if="getContactIdentifier(conversation)"
                class="text-sm text-n-slate-11 truncate"
              >
                {{ getContactIdentifier(conversation) }}
              </div>
            </div>

            <div
              v-if="selectedConversation?.id === conversation.id"
              class="flex-shrink-0"
            >
              <fluent-icon
                icon="checkmark-circle"
                size="20"
                class="text-n-blue-9"
              />
            </div>
          </div>
        </div>
      </div>

      <div class="flex items-center justify-end gap-2 px-6 py-4">
        <NextButton
          variant="ghost"
          color="slate"
          :label="$t('CONVERSATION.FORWARD_MODAL.CANCEL')"
          @click="handleClose"
        />
        <NextButton
          :disabled="!selectedConversation"
          :label="$t('CONVERSATION.FORWARD_MODAL.SUBMIT')"
          @click="handleForward"
        />
      </div>
    </div>
  </woot-modal>
</template>
